import type { Container, Sprite } from 'pixi.js';
import { foxOf } from '../../sim/full/attacks/fox';
import type { ClassDrawing, PaintedDrawing } from '../drawings';
import { type Shape, paintSheet } from '../paint';
import type { Look } from '../player-look';
import type { Textures } from '../textures';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, hide, placeOutline, setTint } from '../util';
import {
  BRANCHES,
  ENRACINE_PIECES,
  FOX_TAIL_AT,
  HANDS,
  HEAD,
  KNEES,
  ROOTS,
  ROOT_LENGTH,
  UNIT,
  branchLength,
} from './enracine-paint';

type Parts = Readonly<Record<keyof typeof ENRACINE_PIECES, Shape>>;

const EXTENT = 36;
const MIDDLE = 8;
const MAX_NOTCHES = 4;
const ROOT_ALPHA = 0.85;
const SWAY = { perBeat: 2, turn: 0.035 } as const;
const HEART = { along: 2, radius: 11, low: 0.3, high: 0.15, beats: 2 } as const;
const FOX_AT = { along: 19, across: 14, size: 0.85 } as const;
export const FOX_GLOW = 14;
export const WAG = { perBeat: 2, turn: 0.4, calm: 0.15 } as const;

export function placeFox(
  body: Sprite,
  tail: Sprite,
  x: number,
  y: number,
  angle: number,
  size: number,
  wag: number,
): void {
  body.position.set(x, y);
  body.rotation = angle;
  body.scale.set(size);
  tail.position.set(
    x + Math.cos(angle) * FOX_TAIL_AT * UNIT * size,
    y + Math.sin(angle) * FOX_TAIL_AT * UNIT * size,
  );
  tail.rotation = angle + wag;
  tail.scale.set(size);
}

function createRootedLook(
  bodies: Container,
  glows: Container,
  textures: Textures,
  parts: Parts,
): Look {
  const roots = Array.from({ length: ROOTS_MAX }, (_, index) =>
    add(bodies, index % 2 === 0 ? parts.rootA : parts.rootB),
  );
  const knees = [add(bodies, parts.knee), add(bodies, parts.knee)];
  const outline = add(bodies, parts.silhouette);
  const body = add(bodies, parts.body);
  const heart = add(glows, textures.halo);
  const hands = [add(bodies, parts.hand), add(bodies, parts.hand)];
  const branches = Array.from({ length: BRANCHES.count }, (_, index) =>
    add(bodies, branchLength(index) > 15 ? parts.branchLong : parts.branchShort),
  );
  const leaves = Array.from({ length: BRANCHES.count }, (_, index) =>
    add(bodies, branchLength(index) > 15 ? parts.leavesLong : parts.leavesShort),
  );
  const head = add(bodies, parts.head);
  const foxGlow = add(glows, textures.halo);
  const foxTail = add(bodies, parts.foxTail);
  const foxBody = add(bodies, parts.foxBody);
  const shown = [...knees, body, ...hands, ...branches, ...leaves, head];
  let originX = 0;
  let originY = 0;
  let cos = 1;
  let sin = 0;
  let size = 1;
  let facing = 0;

  function put(sprite: Sprite, along: number, across: number, turn = 0): void {
    sprite.position.set(
      originX + (along * cos - across * sin) * UNIT * size,
      originY + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = facing + turn;
    sprite.scale.set(size);
    setTint(sprite, 0xffffff);
  }

  return {
    hide() {
      hide(...shown, ...roots, outline, heart, foxGlow, foxTail, foxBody);
    },
    place(input) {
      const { player, state, x, y, angle, scale, color, frame } = input;
      const { now, calm, palette, light, tempo } = frame;
      const beats = now / tempo.ticksPerBeat;
      originX = x;
      originY = y;
      cos = Math.cos(angle);
      sin = Math.sin(angle);
      size = scale;
      facing = angle;
      for (const sprite of shown) {
        sprite.visible = true;
      }

      const notches = frame.portrait ? MAX_NOTCHES : Math.min(MAX_NOTCHES, player.gauge ?? 0);
      const count = ROOTS.count + Math.floor(notches);
      roots.forEach((root, index) => {
        root.visible = index < count;
        if (!root.visible) {
          return;
        }
        const turn = ROOTS.from + (index * ROOTS.spread) / Math.max(1, count - 1);
        const length = ROOTS.base + ROOTS.step * notches + (index % 2) * 4;
        put(root, -2 + Math.cos(turn) * ROOTS.start, Math.sin(turn) * ROOTS.start * 1.05, turn);
        root.scale.set((size * length) / ROOT_LENGTH, size * Math.max(0.6, length / ROOT_LENGTH));
        root.alpha = ROOT_ALPHA;
      });

      for (const [index, side] of [-1, 1].entries()) {
        const knee = knees[index];
        const hand = hands[index];
        if (knee !== undefined && hand !== undefined) {
          put(knee, KNEES.along, side * KNEES.across, -side * KNEES.turn);
          put(hand, HANDS.along, side * HANDS.across);
        }
      }
      put(body, 0, 0);
      placeOutline(outline, body, parts.silhouette.texture, REFERENCE_RADIUS, frame);

      const breath = 0.5 - 0.5 * Math.cos((Math.PI * 2 * beats) / HEART.beats);
      heart.visible = true;
      put(heart, HEART.along, 0);
      heart.scale.set((HEART.radius * UNIT * size) / textures.halo.radius);
      heart.alpha = light.haloAlpha * (HEART.low + HEART.high * (calm ? 0.5 : breath));
      setTint(heart, palette.texte);

      branches.forEach((branch, index) => {
        const leaf = leaves[index];
        if (leaf === undefined) {
          return;
        }
        const sway = calm ? 0 : Math.sin(SWAY.perBeat * beats + index) * SWAY.turn;
        const turn = BRANCHES.from + index * BRANCHES.step + sway;
        put(branch, BRANCHES.root, 0, turn);
        put(leaf, BRANCHES.root, 0, turn);
        setTint(leaf, color);
      });
      put(head, HEAD.along, 0);

      const owned = foxOf(state, player);
      const home = owned === undefined || owned.phase === 'held';
      foxBody.visible = foxTail.visible = foxGlow.visible = home;
      if (home) {
        const wag =
          Math.sin(Math.PI * 2 * WAG.perBeat * beats * (calm ? 0.5 : 1)) *
          (calm ? WAG.calm : WAG.turn);
        put(foxBody, FOX_AT.along, FOX_AT.across);
        placeFox(foxBody, foxTail, foxBody.x, foxBody.y, angle, size * FOX_AT.size, wag);
        setTint(foxTail, 0xffffff);
        foxGlow.position.copyFrom(foxBody.position);
        foxGlow.scale.set((FOX_GLOW * UNIT * size * FOX_AT.size) / textures.halo.radius);
        foxGlow.alpha = 0.35 * light.haloAlpha;
        setTint(foxGlow, palette.tank);
      }
      return 0;
    },
  };
}

const ROOTS_MAX = ROOTS.count + MAX_NOTCHES;

export const ENRACINE_DRAWING: ClassDrawing = {
  classId: 'enracine',
  paint(density): PaintedDrawing {
    const { shapes, source } = paintSheet(ENRACINE_PIECES, density);
    return {
      extent: EXTENT,
      height: 1,
      centerAhead: MIDDLE,
      shadow: shapes.shadow,
      source,
      create: (bodies: Container, glows: Container, textures: Textures) =>
        createRootedLook(bodies, glows, textures, shapes),
    };
  },
};
